/**
 * Komal OS - Expense Management Service
 * Thin wrappers around /api/expenses/* and /api/emails/* (see
 * app/routes/expense_routes.py and app/routes/email_routes.py).
 */

import api from './api';

// Vendors
export const getVendors = (params = {}) => api.get('/expenses/vendors', { params });
export const createVendor = (data) => api.post('/expenses/vendors', data);
export const updateVendor = (id, data) => api.put(`/expenses/vendors/${id}`, data);
export const deactivateVendor = (id) => api.delete(`/expenses/vendors/${id}`);

// Fixed assets
export const getFixedAssets = (params = {}) => api.get('/expenses/fixed-assets', { params });
export const createFixedAsset = (data) => api.post('/expenses/fixed-assets', data);

// Site expenses
export const getSiteAdvances = (params = {}) => api.get('/expenses/site/advances', { params });
export const createSiteAdvance = (data) => api.post('/expenses/site/advances', data);
export const updateSiteAdvance = (id, data) => api.put(`/expenses/site/advances/${id}`, data);
export const deleteSiteAdvance = (id) => api.delete(`/expenses/site/advances/${id}`);
export const getSiteExpenses = (params = {}) => api.get('/expenses/site/expenses', { params });
export const createSiteExpense = (data) => api.post('/expenses/site/expenses', data);
export const createSiteExpensesBatch = (data) => api.post('/expenses/site/expenses/batch', data);
// .xlsx bulk import (2026-08-19): a real dropdown-restricted template, not
// plain CSV - see expense_routes.py's "XLSX bulk-import templates" section
// for why. The file is uploaded as-is; all name resolution and validation
// happens server-side against live data.
export const getSiteExpensesImportTemplate = () => api.get('/expenses/site/expenses/import-template', { responseType: 'blob' });
export const importSiteExpenses = (file) => {
  const form = new FormData();
  form.append('file', file);
  return api.post('/expenses/site/expenses/import', form, { headers: { 'Content-Type': undefined } });
};
export const updateSiteExpense = (id, data) => api.put(`/expenses/site/expenses/${id}`, data);
export const deleteSiteExpense = (id) => api.delete(`/expenses/site/expenses/${id}`);
export const approveSiteExpenseSiteManager = (id) => api.put(`/expenses/site/expenses/${id}/approve-site-manager`);
export const approveSiteExpenseAdmin = (id) => api.put(`/expenses/site/expenses/${id}/approve-admin`);
export const rejectSiteExpense = (id, rejection_reason) => api.put(`/expenses/site/expenses/${id}/reject`, { rejection_reason });
export const getSiteSettlements = () => api.get('/expenses/site/settlements');
export const markSiteSettlementSettled = (engineerId) => api.post(`/expenses/site/settlements/${engineerId}/mark-settled`);

// Factory expenses
export const getFactoryDeposits = (params = {}) => api.get('/expenses/factory/deposits', { params });
export const createFactoryDeposit = (data) => api.post('/expenses/factory/deposits', data);
export const updateFactoryDeposit = (id, data) => api.put(`/expenses/factory/deposits/${id}`, data);
export const deleteFactoryDeposit = (id) => api.delete(`/expenses/factory/deposits/${id}`);
export const getFactoryBills = (params = {}) => api.get('/expenses/factory/bills', { params });
export const createFactoryBill = (data) => api.post('/expenses/factory/bills', data);
export const getFactoryBillsImportTemplate = () => api.get('/expenses/factory/bills/import-template', { responseType: 'blob' });
export const importFactoryBills = (file) => {
  const form = new FormData();
  form.append('file', file);
  return api.post('/expenses/factory/bills/import', form, { headers: { 'Content-Type': undefined } });
};
export const updateFactoryBill = (id, data) => api.put(`/expenses/factory/bills/${id}`, data);
export const deleteFactoryBill = (id) => api.delete(`/expenses/factory/bills/${id}`);
export const approveFactoryBill = (id) => api.put(`/expenses/factory/bills/${id}/approve`);
export const rejectFactoryBill = (id, rejection_reason) => api.put(`/expenses/factory/bills/${id}/reject`, { rejection_reason });
export const getFactorySettlements = () => api.get('/expenses/factory/settlements');
export const markFactorySettlementSettled = (type) => api.post(`/expenses/factory/settlements/${encodeURIComponent(type)}/mark-settled`);

// Vendor payments
export const getVendorAdvances = (params = {}) => api.get('/expenses/vendor/advances', { params });
export const createVendorAdvance = (data) => api.post('/expenses/vendor/advances', data);
export const updateVendorAdvance = (id, data) => api.put(`/expenses/vendor/advances/${id}`, data);
export const deleteVendorAdvance = (id) => api.delete(`/expenses/vendor/advances/${id}`);
export const getVendorBills = (params = {}) => api.get('/expenses/vendor/bills', { params });
export const createVendorBill = (data) => api.post('/expenses/vendor/bills', data);
export const getVendorBillsImportTemplate = () => api.get('/expenses/vendor/bills/import-template', { responseType: 'blob' });
export const importVendorBills = (file) => {
  const form = new FormData();
  form.append('file', file);
  return api.post('/expenses/vendor/bills/import', form, { headers: { 'Content-Type': undefined } });
};
export const updateVendorBill = (id, data) => api.put(`/expenses/vendor/bills/${id}`, data);
export const deleteVendorBill = (id) => api.delete(`/expenses/vendor/bills/${id}`);
export const getVendorSettlements = () => api.get('/expenses/vendor/settlements');
export const markVendorSettlementSettled = (vendorId) => api.post(`/expenses/vendor/settlements/${vendorId}/mark-settled`);

// Material purchases (factory PO-level, one lumpsum per project per month -
// see expense_routes.py's "MATERIAL PURCHASES" section)
export const getMaterialPurchases = (params = {}) => api.get('/expenses/materials/purchases', { params });
export const createMaterialPurchase = (data) => api.post('/expenses/materials/purchases', data);
export const updateMaterialPurchase = (id, data) => api.put(`/expenses/materials/purchases/${id}`, data);
export const deleteMaterialPurchase = (id) => api.delete(`/expenses/materials/purchases/${id}`);
export const getProjectCostSummary = (projectId) =>
  api.get('/expenses/materials/project-cost-summary', { params: { project_id: projectId } });

// Dashboard
export const getDashboardSummary = (params = {}) => api.get('/expenses/dashboard/summary', { params });
export const getDashboardCharts = (params = {}) => api.get('/expenses/dashboard/charts', { params });
export const getDashboardBudget = () => api.get('/expenses/dashboard/budget');
export const getDashboardBudgetVsActual = (params = {}) => api.get('/expenses/dashboard/budget-vs-actual', { params });
export const getDashboardAlerts = (params = {}) => api.get('/expenses/dashboard/alerts', { params });

// CSV export - these return raw text; download by opening the API URL
// directly (with the auth token) rather than routing the blob through axios.
export const EXPORT_ENDPOINTS = {
  projects: '/expenses/export/projects',
  engineers: '/expenses/export/engineers',
  vendors: '/expenses/export/vendors',
  all: '/expenses/export/all',
};
export const exportCsv = (key) => api.get(EXPORT_ENDPOINTS[key], { responseType: 'blob' });

// Email preferences / logs
export const getEmailPreferences = () => api.get('/emails/preferences');
export const updateEmailPreferences = (data) => api.put('/emails/preferences', data);
export const getEmailLogs = () => api.get('/emails/logs');
export const sendWeeklySummaryNow = () => api.post('/emails/send-weekly-summary');
